import { DckActionTypes } from "dck-redux"
import moment from "moment"
import { all, put, takeEvery } from "redux-saga/effects"
import {
  addUniqueValue,
  CALCULATION_TYPE,
  capitalFirstCharacter,
  convertToObject,
  fineractDateToStr,
  fineractToDate,
  formatCurrency,
  HTTP_VERB,
  TransactionEntryType
} from "../../../lib/utils"
import * as ItemTypes from "../../items/types"
import apiServerConstants from "../apiServerConstants"
import { makeSagaQuery } from "../utils"

export const getTransactionItem = elt => {

  const { transactionDate, createdDate, glAccountType: { value: glAccountType },
  glAccountName, glAccountCode, glAccountId, entryType: { value: entryType }, manualEntry,
  amount, id, transactionId, comments, currency: { code: currencyCode }, reversed } = elt,

  item = { amount, transactionId, comments, manualEntry, glAccountId, glAccountCode, currencyCode,
    entryType, glAccountType, reversed }

  item.id = '' + id
  item.transactionDateFormatted = fineractDateToStr(transactionDate)
  item.transactionDate = fineractToDate(transactionDate)
  item.createdOn = fineractToDate(createdDate)
  item.createdOnFormatted = fineractDateToStr(createdDate)
  item.glAccountName = `${glAccountName} (${glAccountCode})`
  item.debitsFormatted = entryType === TransactionEntryType.DEBIT ? formatCurrency(amount) : ''
  item.creditsFormatted = entryType === TransactionEntryType.CREDIT ? formatCurrency(amount) : ''

  return item
}

function *loadGLAccountSaga() {

  yield makeSagaQuery(
    `${apiServerConstants.host}/glaccounts?page=${0}&size=${1000000}`, 
    HTTP_VERB.GET,
    null,
    ItemTypes.GLAccount,
    true,
    response => {

      let data = response.body
      if(!(data instanceof Array)) return

      data = data.map(item => {

        const { type, usage, createdOn } = item
        item.typeFormatted = capitalFirstCharacter(type)
        item.usageFormatted = capitalFirstCharacter(usage)
        item.createdOnFormatted = createdOn ? moment(createdOn).format('ll') : ''
        return item
      })

      return {
        type: DckActionTypes.ITEMS_SET,
        itemType: ItemTypes.GLAccount,
        data
      }
    }
  )
}

function *loadGLClosureSaga() {

  yield makeSagaQuery(
    `${apiServerConstants.host}/glclosures?page=${0}&size=${1000000}`, 
    HTTP_VERB.GET,
    null,
    ItemTypes.GLClosure,
    true,
    response => {

      let data = response.body
      if(!(data instanceof Array)) return

      data = data.map(item => {

        const { closingDate, createdOn } = item
        item.closingDateFormatted = closingDate ? moment(closingDate).format('ll') : ''
        item.createdOnFormatted = createdOn ? moment(createdOn).format('ll') : ''
        return item
      })

      return {
        type: DckActionTypes.ITEMS_SET,
        itemType: ItemTypes.GLClosure,
        data
      }
    }
  )
}

function *loadJournalEntriesSaga({ filteringOptions }) {

  yield makeSagaQuery(
    `${apiServerConstants.host}/journalentries/list`,
    HTTP_VERB.POST,
    JSON.stringify(filteringOptions),
    ItemTypes.JournalEntry,
    true,
    response => {

      const data = {
        content: [],
        currentPage: filteringOptions.offset,
        pageSize: filteringOptions.limit,
        totalElements: 0,
      }

      try {
        const jsonData = JSON.parse(response.body)
        data.content = jsonData.pageItems.map(getTransactionItem)
        data.totalElements = jsonData.totalFilteredRecords
      }
      catch (error) {
        console.error(error)
      }

      return {
        type: DckActionTypes.ITEMS_SET,
        itemType: ItemTypes.JournalEntry,
        data
      }
    }
  )
}

function *loadAccountingRuleSaga() {

  yield makeSagaQuery(
    `${apiServerConstants.host}/accountingrules?page=${0}&size=${1000000}`, 
    HTTP_VERB.GET,
    null,
    ItemTypes.AccountingRule,
    true,
    response => {

      let data = response.body
      if(!(data instanceof Array)) return

      data = data.map(item => {

        const { accountToDebit, accountToCredit, createdOn } = item

        item.accountToDebitName = accountToDebit ? accountToDebit.name : ''
        item.accountToCreditName = accountToCredit ? accountToCredit.name : ''
        item.createdOnFormatted = createdOn ? moment(createdOn).format('ll') : ''

        return item
      })

      return {
        type: DckActionTypes.ITEMS_SET,
        itemType: ItemTypes.AccountingRule,
        data
      }
    }
  )
}

function *loadLoanCommissionSaga() {

  const accountToDebits = [],
  accountToCredits = []

  yield makeSagaQuery(
    `${apiServerConstants.host}/loancommissions?page=${0}&size=${1000000}`, 
    HTTP_VERB.GET,
    null,
    ItemTypes.LoanCommission,
    true,
    response => {

      let data = response.body
      if(!(data instanceof Array)) return

      data = data.map(item => {

        const { accountToDebit, accountToCredit, calculationType, amount, createdOn  } = item

        item.accountToDebitName = accountToDebit ? accountToDebit.name : ''
        item.accountToCreditName = accountToCredit ? accountToCredit.name : ''
        item.calculationTypeFormatted = capitalFirstCharacter(calculationType)
        item.amountFormatted = `${ amount } ${ calculationType === CALCULATION_TYPE.FLAT ? '' : '%' }`
        item.createdOnFormatted = createdOn ? moment(createdOn).format('ll') : ''

        addUniqueValue(accountToDebits, item.accountToDebitName)
        addUniqueValue(accountToCredits, item.accountToCreditName)

        return item
      })

      return {
        type: DckActionTypes.ITEMS_SET,
        itemType: ItemTypes.LoanCommission,
        data
      }
    }
  )

  yield put({
    type: DckActionTypes.ITEMS_SET,
    itemType: ItemTypes.AccountToDebitsFilter,
    data: [ convertToObject(accountToDebits) ]
  })

  yield put({
    type: DckActionTypes.ITEMS_SET,
    itemType: ItemTypes.AccountToCreditsFilter,
    data: [ convertToObject(accountToCredits) ]
  })
}

function *loadCommissionLogsSaga() {

  yield makeSagaQuery(
    `${apiServerConstants.host}/commissionlogs?page=${0}&size=${1000000}`, 
    HTTP_VERB.GET,
    null,
    ItemTypes.CommissionLog,
    true,
    response => {

      let data = response.body
      if(!(data instanceof Array)) return

      data = data.map(item => {
        const { createdOn } = item
        item.createdOnFormatted = createdOn ? moment(createdOn).format('ll') : ''
        return item
      })

      return {
        type: DckActionTypes.ITEMS_SET,
        itemType: ItemTypes.CommissionLog,
        data
      }
    }
  )
}

function* AccountingsSaga() {

  yield all([

    takeEvery(
      action =>
        action.type === DckActionTypes.ITEMS_LOAD &&
        action.itemType === ItemTypes.GLAccount,
      loadGLAccountSaga
    ),

    takeEvery(
      action =>
        action.type === DckActionTypes.ITEMS_LOAD &&
        action.itemType === ItemTypes.GLClosure,
      loadGLClosureSaga
    ),

    takeEvery(
      action =>
        action.type === DckActionTypes.ITEMS_LOAD &&
        action.itemType === ItemTypes.JournalEntry,
      loadJournalEntriesSaga
    ),

    takeEvery(
      action =>
        action.type === DckActionTypes.ITEMS_LOAD &&
        action.itemType === ItemTypes.AccountingRule,
      loadAccountingRuleSaga
    ),

    takeEvery(
      action =>
        action.type === DckActionTypes.ITEMS_LOAD &&
        action.itemType === ItemTypes.LoanCommission,
      loadLoanCommissionSaga
    ),

    takeEvery(
      action =>
        action.type === DckActionTypes.ITEMS_LOAD &&
        action.itemType === ItemTypes.CommissionLog,
      loadCommissionLogsSaga
    ),
  ])
}

export default AccountingsSaga



// WEBPACK FOOTER //
// ./src/redux/sagas/items/accounting.js